/* eslint-disable react/forbid-prop-types */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { apiGetModel, baseRequestParams } from '../../useApiHook';
import { callAxiosParallel, callAxios } from '../../../services/callAPIMiddleware';
import _ from '../../../utils/lodash';
import globals from '../../../globals/communityGlobals';

import {
  mapPlanningDetails,
  mapClinicianTreatments,
  mapMapViewDetails,
  mapClinicianDetails,
  mapNonPatientActivityDetails,
  mapOptions,
} from './mappingDtos';

const errorMessages = globals.getArea('error');

const overrideApiUrl = globals.urls.autoAllocateApiUrl;

const handleErrorMessage = exception => {
  if (exception.message.localeCompare(errorMessages.getMsg('cancelDuplicateRequest')) !== 0) {
    return exception.response.data !== null && exception.response.data.translationKey !== ''
      ? exception.response.data.translationKey
      : exception.message;
  }

  return null;
};

export const useFetchPlanningDetailsOld = (includeTreatments = false) => {
  // Prepare api model and request call
  const baseRequestModel = baseRequestParams(false, includeTreatments);
  const model = apiGetModel('OldGetClinicianPlanningDetails', baseRequestModel);
  const auth = useSelector(state => state.global.auth);
  const externalProsValue = `${baseRequestModel.SelectedDate}_${baseRequestModel.OrgUnitId}_${baseRequestModel.IsNightMode}`;
  const [state, setState] = useState({
    response: null,
    isDataLoading: true,
    isUpdateTriggered: false,
    isAllocation: false,
    url: '', // kept the previous url for preventing doble requests
    error: null,
    externalPros: externalProsValue,
  });

  useEffect(() => {
    const triggerRequest = state.isUpdateTriggered || model.url !== state.url;
    if (triggerRequest) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result =>
          setState({
            ...state,
            response: result.data,
            isDataLoading: false,
            isUpdateTriggered: false,
            isAllocation: false,
            url: model.url,
            externalPros: externalProsValue,
          }),
        )
        .catch(ex => {
          setState({
            ...state,
            isDataLoading: false,
            isUpdateTriggered: false,
            isAllocation: false,
            error: handleErrorMessage(ex),
            externalPros: externalProsValue,
          });
        });
    }
  }, [model.url, state.isUpdateTriggered]);

  // Returning strongly-type mappedDto object
  return {
    data: mapPlanningDetails(state.response),
    isDataLoading: state.isDataLoading,
    isUpdateTriggered: state.isUpdateTriggered || state.externalPros !== externalProsValue,
    error: state.error,
    baseRequestModel,
    refresh: () => setState(ns => ({ ...ns, isUpdateTriggered: true })),
  };
};

export const useFetchPlanningDetails = (includeTreatments = false) => {
  // Prepare api model and request call
  const baseRequestModel = baseRequestParams(false, includeTreatments);
  const clinicianPlanningModel = apiGetModel('GetClinicianPlanningDetails', baseRequestModel);
  const treatmentsPlanningModel = apiGetModel('GetClinicianPlanningTreatmentsDetails', baseRequestModel);

  const auth = useSelector(state => state.global.auth);
  const externalProsValue = `${baseRequestModel.SelectedDate}_${baseRequestModel.OrgUnitId}_${baseRequestModel.IsNightMode}`;
  const [state, setState] = useState({
    response: null,
    isDataLoading: true,
    isUpdateTriggered: false,
    isAllocation: false,
    url: '', // kept the previous url for preventing doble requests
    error: null,
    externalPros: externalProsValue,
  });

  useEffect(() => {
    const triggerRequest = state.isUpdateTriggered || clinicianPlanningModel.url !== state.url;
    if (triggerRequest) {
      callAxiosParallel(auth, [clinicianPlanningModel, treatmentsPlanningModel])
        .then(result => {
          const cliniciansData = result.find(r => r.config.url.includes(clinicianPlanningModel.url)).data;
          const treatmentsData = result.find(r => r.config.url.includes(treatmentsPlanningModel.url)).data;
          const mergedData = { ...cliniciansData, ...treatmentsData };
          mergedData.summaryHoursStats = [...cliniciansData.summaryHoursStats, ...treatmentsData.summaryHoursStats];
          setState({
            ...state,
            response: mergedData,
            isDataLoading: false,
            isUpdateTriggered: false,
            isAllocation: false,
            url: clinicianPlanningModel.url,
            externalPros: externalProsValue,
          });
        })
        .catch(ex => {
          setState({
            ...state,
            isDataLoading: false,
            isUpdateTriggered: false,
            isAllocation: false,
            error: handleErrorMessage(ex),
            externalPros: externalProsValue,
          });
        });
    }
  }, [clinicianPlanningModel.url, state.isUpdateTriggered]);

  // Returning strongly-type mappedDto object
  return {
    data: mapPlanningDetails(state.response),
    isDataLoading: state.isDataLoading,
    isUpdateTriggered: state.isUpdateTriggered || state.externalPros !== externalProsValue,
    error: state.error,
    baseRequestModel,
    refresh: () => setState(ns => ({ ...ns, isUpdateTriggered: true })),
  };
};

export const useFetchClinicianTreatments = (clinicianId, postingId, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [response, setResponse] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams();
  const model = apiGetModel(
    'GetAvailableVisitsPerClinician',
    _.merge({ ClinicianId: clinicianId, PostingId: postingId }, baseRequestModel),
    forceReload,
  );
  useEffect(() => {
    if (jwtToken) {
      if (!isDataLoading) setIsDataLoading(true);
      callAxios(auth, model.url, model.method, model.data)
        .then(result => {
          setResponse(result.data);
          setIsDataLoading(false);
        })
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setIsDataLoading(false);
        });
    }
  }, [jwtToken, model.url, model.reloadData]);
  // Returning strongly-type mappedDto object
  return {
    data: mapClinicianTreatments(response),
    isDataLoading,
    error,
    baseRequestModel,
  };
};

export const useFetchVisitDetails = (treatmentInstanceId, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [response, setResponse] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams();
  const model = apiGetModel(
    'GetVisitDetails',
    _.merge({ TreatmentInstanceId: treatmentInstanceId }, baseRequestModel),
    forceReload,
  );

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => {
          setResponse(result.data);
          setIsDataLoading(false);
        })
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setIsDataLoading(false);
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: response,
    isDataLoading,
    error,
    baseRequestModel,
    auth,
  };
};

export const useFetchUnassignedVisitDetails = (
  treatmentInstanceId,
  treatmentId,
  timeSlotId,
  selectedDate,
  forceReload,
) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [response, setResponse] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams();
  const model = apiGetModel(
    'GetTreatmentDetails',
    _.merge(
      {
        TreatmentInstanceId: treatmentInstanceId,
        TreatmentId: treatmentId,
        SelectedDate: selectedDate,
        TimeSlotId: timeSlotId,
      },
      baseRequestModel,
    ),
    forceReload,
  );

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => {
          setResponse(result.data);
          setIsDataLoading(false);
        })
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setIsDataLoading(false);
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: response,
    isDataLoading,
    error,
    baseRequestModel,
  };
};

export const useFetchMapViewDetails = forceRefresh => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceRefresh });
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams(true);
  const model = apiGetModel('GetClinicianPlanningDetails', baseRequestModel, forceRefresh);

  const setRequestResponse = response => setState({ ...state, response, isDataLoading: false, refresh: false });

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setState({ isDataLoading: false });
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return { data: mapMapViewDetails(state.response), isDataLoading: state.isDataLoading, error, baseRequestModel };
};

export const useFetchClinicianDetails = (clinicianId, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [response, setResponse] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams();
  const model = apiGetModel(
    'GetClinicianDetails',
    _.merge({ ClinicianId: clinicianId }, baseRequestModel),
    forceReload,
  );

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => {
          setResponse(result.data);
          setIsDataLoading(false);
        })
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setIsDataLoading(false);
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: mapClinicianDetails(response),
    isDataLoading,
    error,
    baseRequestModel,
  };
};

export const useFetchCancellationReasonsOptions = forceRefresh => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceRefresh });
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams(true);
  const model = apiGetModel('GetCancellationReasonsOptions', baseRequestModel, forceRefresh);

  const setRequestResponse = response => setState({ ...state, response, isDataLoading: false, refresh: false });

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setState({ isDataLoading: false });
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: mapOptions(state.response),
    isDataLoading: state.isDataLoading,
    error,
    baseRequestModel,
  };
};

export const useFetchCancellingPartiesOptions = forceRefresh => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceRefresh });
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams(true);
  const model = apiGetModel('GetCancellingPartiesOptions', baseRequestModel, forceRefresh);

  const setRequestResponse = response => setState({ ...state, response, isDataLoading: false, refresh: false });

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setState({ isDataLoading: false });
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: mapOptions(state.response),
    isDataLoading: state.isDataLoading,
    error,
    baseRequestModel,
  };
};

export const useFetchNonPatientAssignmentDetails = (npaId, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [response, setResponse] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const baseRequestModel = baseRequestParams();
  const model = apiGetModel('GetNonPatientActivityById', _.merge({ Id: npaId }, baseRequestModel), forceReload);

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, model.url, model.method, model.data)
        .then(result => {
          setResponse(result.data);
          setIsDataLoading(false);
        })
        .catch(ex => {
          const errorMsg = handleErrorMessage(ex);
          if (errorMsg !== null) {
            setError(errorMsg);
          }
          setIsDataLoading(false);
        });
    }
  }, [jwtToken, model.url, model.reloadData]);

  // Returning strongly-type mappedDto object
  return {
    data: mapNonPatientActivityDetails(response),
    isDataLoading,
    error,
    baseRequestModel,
    auth,
  };
};

export const useAutoAllocationGroups = (model, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceReload });
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const getModel = apiGetModel('GetAutoAllocationGroups', model, forceReload);
  const setRequestResponse = response => {
    setState({ ...state, response, isDataLoading: false, refresh: forceReload });
  };

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, getModel.url, getModel.method, getModel.data, overrideApiUrl)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          setError(ex.mesage);
          setState({ ...state, isDataLoading: false });
        });
    }
  }, [jwtToken, getModel.url, getModel.reloadData]);

  return {
    data: state.response ? state.response.groups : [],
    isDataLoading: state.isDataLoading,
    error,
  };
};

export const useAutoAllocationLogs = (model, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceReload });
  const [error, setError] = useState(null);

  // Prepare api model and request call
  const getModel = apiGetModel('GetAutoAllocationLogs', model, forceReload);
  const setRequestResponse = response => {
    setState({ ...state, response, isDataLoading: false, refresh: forceReload });
  };

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, getModel.url, getModel.method, getModel.data, overrideApiUrl)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          setError(ex.mesage);
          setState({ ...state, isDataLoading: false });
        });
    }
  }, [jwtToken, getModel.url, getModel.reloadData]);

  return {
    data: state.response ? state.response.logs : [],
    isDataLoading: state.isDataLoading,
    error,
  };
};

export const useFetchActivitiesOptions = (model, forceReload) => {
  const auth = useSelector(state => state.global.auth);
  const jwtToken = auth.jwtToken;
  const [state, setState] = useState({ response: null, isDataLoading: true, refresh: forceReload });
  const [error, setError] = useState(null);

  const getModel = apiGetModel('GetActivitiesOptions', model, forceReload);
  const setRequestResponse = response => {
    setState({ ...state, response, isDataLoading: false, refresh: forceReload });
  };

  useEffect(() => {
    if (jwtToken) {
      callAxios(auth, getModel.url, getModel.method, getModel.data)
        .then(result => setRequestResponse(result.data))
        .catch(ex => {
          setError(ex.mesage);
          setState({ ...state, isDataLoading: false });
        });
    }
  }, [jwtToken, getModel.url, getModel.reloadData]);

  return {
    data: state.response ? state.response.options : [],
    isDataLoading: state.isDataLoading,
    error,
  };
};
